import { useState, useEffect } from "react"
import {
  Building2,
  Plus,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Users,
  MapPin,
  Calendar,
  Loader2,
  Phone,
  Mail,
  Save,
  Tag,
  GripVertical,
  Target,
} from "lucide-react"
import { HRService } from "@/features/hr"
import type { Organization } from "@/features/organizations"
import { OrganizationShimmerTable } from "@/shared/components"
import { useAuth } from "@/features/auth"

interface OrganizationManagerProps {
  onError: (error: string) => void
  onSuccess?: (message: string) => void
  organizationId?: string
}

interface CreateOrganizationForm {
  name: string
  type: string
  parentId?: string
  settings: {
    description?: string
    address?: string
    phone?: string
    email?: string
  }
}

export default function OrganizationManager({ onError, onSuccess, organizationId }: OrganizationManagerProps) {
  const { user } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [availableTypes, setAvailableTypes] = useState<string[]>([])
  const [hierarchy, setHierarchy] = useState<any>({})
  const [parentOrganizations, setParentOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [loadingParents, setLoadingParents] = useState(false)
  
  // Cascading parent selection state
  const [selectedEMParent, setSelectedEMParent] = useState<string>("")
  const [selectedDMParent, setSelectedDMParent] = useState<string>("")
  const [selectedCMParent, setSelectedCMParent] = useState<string>("")
  const [selectedBMParent, setSelectedBMParent] = useState<string>("")
  
  // Available organizations at each level
  const [availableEMOrgs, setAvailableEMOrgs] = useState<Organization[]>([])
  const [availableDMOrgs, setAvailableDMOrgs] = useState<Organization[]>([])
  const [availableCMOrgs, setAvailableCMOrgs] = useState<Organization[]>([])
  const [availableBMOrgs, setAvailableBMOrgs] = useState<Organization[]>([])
  const [loadingEdit, setLoadingEdit] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [loadingViewDetails, setLoadingViewDetails] = useState<string | null>(null)
  const [updating, setUpdating] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showReassignModal, setShowReassignModal] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [reassignToOrgId, setReassignToOrgId] = useState("")
  const [organizationDependencies, setOrganizationDependencies] = useState<{
    agents: number
    teams: number
    campaigns: number
    children: number
  } | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [typeFilter, setTypeFilter] = useState("all")
  const [sortField, setSortField] = useState<keyof Organization | 'emLevel' | 'dmLevel' | 'cmLevel' | 'bmLevel' | 'amLevel'>("createdAt")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc")
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  
  // Hierarchy level filters
  const [emFilter, setEmFilter] = useState("all")
  const [dmFilter, setDmFilter] = useState("all")
  const [cmFilter, setCmFilter] = useState("all")
  const [bmFilter, setBmFilter] = useState("all")
  const [amFilter, setAmFilter] = useState("all")

  // Organization List ID Management State
  const [showListIdModal, setShowListIdModal] = useState(false)
  const [listIdStats, setListIdStats] = useState<Array<{ listId: string, count: number }>>([])
  const [organizationsByListId, setOrganizationsByListId] = useState<{ [key: string]: Organization[] }>({})
  const [loadingListIds, setLoadingListIds] = useState(false)
  const [draggedOrganization, setDraggedOrganization] = useState<Organization | null>(null)
  const [successfulUpdate, setSuccessfulUpdate] = useState<string | null>(null)

  // List ID Management State (FMO only)
  const [listIds, setListIds] = useState<Array<{ id: string, name: string, isActive: boolean, createdAt: string, color: string }>>([])
  const [hasError, setHasError] = useState(false)

  const [listIdModalOrganizations, setListIdModalOrganizations] = useState<Organization[]>([])

  // Fallback function to get organization type display names
  const getTypeDisplayName = (type: string) => {
    const typeNames: Record<string, string> = {
      FMO: "FMO",
      EM: "EM",
      DM: "DM",
      CM: "CM",
      BM: "BM",
      AM: "AM",
      M: "M"
    }
    return typeNames[type] || type
  }

  const getAvailableParentLevels = (type: string): string[] => {
    switch (type) {
      case 'AM': return ['BM', 'CM', 'DM', 'EM']
      case 'BM': return ['CM', 'DM', 'EM']
      case 'CM': return ['DM', 'EM']
      case 'DM': return ['EM']
      case 'EM': return []
      case 'FMO': return []
      default: return []
    }
  }


  const getNextLevelToShow = (type: string): string | null => {
    const availableLevels = getAvailableParentLevels(type)
    if (availableLevels.length === 0) return null
    
    // Only show the first available parent level (immediate parent)
    return availableLevels[0] || null
  }

  const getNextLevelToShowEdit = (type: string): string | null => {
    const availableLevels = getAvailableParentLevels(type)
    if (availableLevels.length === 0) return null
    
    // Only show the first available parent level (immediate parent)
    return availableLevels[0] || null
  }

  const [formData, setFormData] = useState<CreateOrganizationForm>({
    name: "",
    type: "",
    parentId: undefined,
    settings: {
      description: "",
      address: "",
      phone: "",
      email: "",
    },
  })

  const [editFormData, setEditFormData] = useState<CreateOrganizationForm>({
    name: "",
    type: "",
    parentId: undefined,
    settings: {
      description: "",
      address: "",
      phone: "",
      email: "",
    },
  })

  const [formErrors, setFormErrors] = useState<Partial<CreateOrganizationForm>>({})
  const [editFormErrors, setEditFormErrors] = useState<Partial<CreateOrganizationForm>>({})

  // Load organizations and available types
  useEffect(() => {
    loadOrganizations()
    loadAvailableTypes()
  }, [])

  // Reload data when modal opens to ensure fresh data from backend
  useEffect(() => {
    if (showListIdModal) {
      setLoadingListIds(true)
      // Load in sequence to ensure listIds are loaded before loading organizations by ListId
      const loadData = async () => {
        try {
          // First load ListIds
          await loadListIds()
          // Then load stats and organizations by ListId (which depend on listIds)
          await Promise.all([
            loadListIdStats(),
            loadOrganizationsByListId(),
            loadEMOrganizationsForListId() // Load only EM level organizations for list ID modal
          ])
        } catch (error) {
          onError("Failed to load organizations")
          console.error("Error loading organizations:", error)
          setOrganizations([])
        } finally {
          setLoadingListIds(false)
        }
      }
      loadData()
    }
  }, [showListIdModal])
  

  const loadEMOrganizationsForListId = async () => {
    try {
      setLoadingListIds(true)
      const orgs = await HRService.getOrganizations(organizationId)
      setListIdModalOrganizations(orgs)
    } catch (error) {
      onError("Failed to load EM organizations")
      console.error("Error loading EM organizations:", error)
      setListIdModalOrganizations([])
    } finally {
      setLoadingListIds(false)
    }
  }


  const loadOrganizations = async () => {
    try {
      setLoading(true)
      setHasError(false)
      const orgs = await HRService.getOrganizations(organizationId)
      setOrganizations(Array.isArray(orgs) ? orgs : [])
    } catch (error) {
      onError("Failed to load organizations")
      console.error("Error loading organizations:", error)
      // Ensure organizations is always an array, even on error
      setOrganizations([])
      setHasError(true)
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableTypes = async () => {
    try {
      const response = await HRService.getAvailableOrganizationTypes()
      setAvailableTypes(response.data?.availableTypes || [])
      setHierarchy(response.data?.hierarchy || {})
    } catch (error) {
      onError("Failed to load organization types")
      console.error("Error loading organization types:", error)
      // Set default values on error
      setAvailableTypes([])
      setHierarchy({})
    }
  }

  const loadParentOrganizations = async (type: string) => {
    if (!type) {
      setParentOrganizations([])
      setAvailableEMOrgs([])
      setAvailableDMOrgs([])
      setAvailableCMOrgs([])
      setAvailableBMOrgs([])
      setLoadingParents(false)
      return
    }

    try {
      setLoadingParents(true)
      const allOrgs = await HRService.getOrganizations(organizationId)
      const orgsArray = Array.isArray(allOrgs) ? allOrgs : []

      // For EM and FMO, no parent needed
      if (type === 'EM' || type === 'FMO') {
        setParentOrganizations([])
        setAvailableEMOrgs([])
        setAvailableDMOrgs([])
        setAvailableCMOrgs([])
        setAvailableBMOrgs([])
        setLoadingParents(false)
        return
      }

      // Load EM level organizations (top level)
      const emOrgs = orgsArray.filter((org: Organization) => org.type === 'EM')
      setAvailableEMOrgs(emOrgs)

      // For DM, only EM selection needed
      if (type === 'DM') {
        setParentOrganizations(emOrgs)
      }

      // For CM, BM, AM - will load cascading options as user selects
      if (type === 'CM' || type === 'BM' || type === 'AM') {
        setParentOrganizations(orgsArray)
      }
    } catch (error) {
      onError("Failed to load parent organizations")
      console.error("Error loading parent organizations:", error)
      setParentOrganizations([])
    } finally {
      setLoadingParents(false)
    }
  }

  // Load DM organizations under selected EM
  const loadDMOrganizations = (emId: string) => {
    if (!emId) {
      setAvailableDMOrgs([])
      setSelectedDMParent("")
      setSelectedCMParent("")
      setSelectedBMParent("")
      return
    }

    const dmOrgs = parentOrganizations.filter(
      (org: Organization) => org.type === 'DM' && org.parentId === emId
    )
    setAvailableDMOrgs(dmOrgs)
  }

  // Load CM organizations under selected DM
  const loadCMOrganizations = (dmId: string) => {
    if (!dmId) {
      setAvailableCMOrgs([])
      setSelectedCMParent("")
      setSelectedBMParent("")
      return
    }

    const cmOrgs = parentOrganizations.filter(
      (org: Organization) => org.type === 'CM' && org.parentId === dmId
    )
    setAvailableCMOrgs(cmOrgs)
  }

  // Load BM organizations under selected CM
  const loadBMOrganizations = (cmId: string) => {
    if (!cmId) {
      setAvailableBMOrgs([])
      setSelectedBMParent("")
      return
    }

    const bmOrgs = parentOrganizations.filter(
      (org: Organization) => org.type === 'BM' && org.parentId === cmId
    )
    setAvailableBMOrgs(bmOrgs)
  }

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    const errors: Partial<CreateOrganizationForm> = {}
    if (!formData.name.trim()) errors.name = "Organization name is required"
    if (!formData.type) errors.type = "Organization type is required"

    setFormErrors(errors)

    if (Object.keys(errors).length > 0) return

    try {
      setCreating(true)


      const response = await HRService.createOrganization({
        name: formData.name,
        type: formData.type,
        parentId: formData.parentId,
        settings: formData.settings,
      })

      // Reload organizations from server to get fresh data
      await loadOrganizations()

      // Reset form
      clearCreateForm()
      setShowCreateForm(false)

      if (onSuccess) {
        onSuccess("Organization created successfully!")
      } else {
        onError("Organization created successfully!")
      }
    } catch (error: any) {
      console.error("Error creating organization:", error)

      let errorMessage = "Failed to create organization"
      
      // Extract error message from various possible response formats
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.response?.data?.data?.message) {
        errorMessage = error.response.data.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      // If it's a duplicate name error (409), show it on the name field as well
      if (error?.response?.status === 409 && errorMessage.toLowerCase().includes('already exists')) {
        setFormErrors(prev => ({
          ...prev,
          name: errorMessage
        }))
      }

      onError(errorMessage)
    } finally {
      setCreating(false)
    }
  }

  const handleInputChange = (field: keyof CreateOrganizationForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleSettingsChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, [field]: value }
    }))
  }

  const handleEditInputChange = (field: keyof CreateOrganizationForm, value: string) => {
    setEditFormData(prev => ({ ...prev, [field]: value }))
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleEditSettingsChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, [field]: value }
    }))
  }

  const handleTypeChange = (type: string) => {
    setFormData(prev => ({ ...prev, type, parentId: undefined }))
    loadParentOrganizations(type)
  }

  const clearCreateForm = () => {
    setFormData({
      name: "",
      type: "",
      parentId: undefined,
      settings: {
        description: "",
        address: "",
        phone: "",
        email: "",
      },
    })
    setFormErrors({})
    setParentOrganizations([])
    
    // Clear cascading selections
    setSelectedEMParent("")
    setSelectedDMParent("")
    setSelectedCMParent("")
    setSelectedBMParent("")
    setAvailableEMOrgs([])
    setAvailableDMOrgs([])
    setAvailableCMOrgs([])
    setAvailableBMOrgs([])
  }

  const handleSort = (field: keyof Organization | 'emLevel' | 'dmLevel' | 'cmLevel' | 'bmLevel' | 'amLevel') => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const getOrganizationTypeBadge = (type: string) => {
    const colors = {
      FMO: "bg-indigo-100 text-indigo-800",
      EM: "bg-purple-100 text-purple-800",
      DM: "bg-blue-100 text-blue-800",
      CM: "bg-green-100 text-green-800",
      BM: "bg-orange-100 text-orange-800",
      AM: "bg-red-100 text-red-800",
      M: "bg-gray-100 text-gray-800",
    }
    return colors[type as keyof typeof colors] || "bg-gray-100 text-gray-800"
  }

  const getSortIcon = (field: keyof Organization | 'emLevel' | 'dmLevel' | 'cmLevel' | 'bmLevel' | 'amLevel') => {
    if (sortField !== field) {
      // Show neutral sort indicator for sortable columns
      return <span className="text-gray-400 text-sm">↕</span>
    }
    return sortDirection === "asc" ? <ChevronUp className="w-4 h-4 text-primary-600" /> : <ChevronDown className="w-4 h-4 text-primary-600" />
  }

  // View organization details
  const handleViewOrganization = async (organization: Organization) => {
    try {
      setLoadingViewDetails(organization.id)
      setSelectedOrganization(organization)
      setShowViewModal(true)
      setIsEditing(false) // Reset editing state

      // Initialize edit form data with current organization data
      setEditFormData({
        name: organization.name,
        type: organization.type,
        parentId: organization.parentId || undefined,
        settings: organization.settings || {
          description: "",
          address: "",
          phone: "",
          email: "",
        },
      })
      setEditFormErrors({})

      // Fetch detailed organization data
      const response = await HRService.getOrganizationById(organization.id)
      setSelectedOrganization(response.data)

      // Update edit form data with the detailed data
      setEditFormData({
        name: response.data.name,
        type: response.data.type,
        parentId: response.data.parentId || undefined,
        settings: response.data.settings || {
          description: "",
          address: "",
          phone: "",
          email: "",
        },
      })
    } catch (error: any) {
      console.error("Error fetching organization details:", error)
      onError("Failed to fetch organization details")
    } finally {
      setLoadingViewDetails(null)
    }
  }

  // Edit organization
  const handleEditOrganization = async (organization: Organization) => {
    try {
      setLoadingEdit(organization.id)
      setFormData({
        name: organization.name,
        type: organization.type,
        parentId: organization.parentId || undefined,
        settings: organization.settings || {
          description: "",
          address: "",
          phone: "",
          email: "",
          website: "",
        },
      })

      // Load parent organizations for the current type
      await loadParentOrganizations(organization.type)

      setSelectedOrganization(organization)
      setShowEditModal(true)
    } catch (error: any) {
      console.error("Error loading organization for edit:", error)
      onError("Failed to load organization for editing")
    } finally {
      setLoadingEdit(null)
    }
  }

  // Update organization
  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedOrganization) return

    // Validate form
    const errors: Partial<CreateOrganizationForm> = {}
    if (!editFormData.name.trim()) errors.name = "Organization name is required"
    if (!editFormData.type) errors.type = "Organization type is required"

    setEditFormErrors(errors)

    if (Object.keys(errors).length > 0) return

    try {
      setUpdating(selectedOrganization.id)
      const response = await HRService.updateOrganization(selectedOrganization.id, {
        name: editFormData.name,
        type: editFormData.type,
        parentId: editFormData.parentId,
        settings: editFormData.settings,
      })


      // Reload organizations from backend to get the latest data
      await loadOrganizations()

      // Close the modal after successful update
      setShowViewModal(false)
      setSelectedOrganization(null)
      setIsEditing(false)

      if (onSuccess) {
        onSuccess("Organization updated successfully!")
      } else {
        onError("Organization updated successfully!")
      }
    } catch (error: any) {
      console.error("Error updating organization:", error)

      let errorMessage = "Failed to update organization"
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      onError(errorMessage)
    } finally {
      setUpdating(null)
    }
  }

  // Delete organization
  const handleDeleteOrganization = async () => {
    if (!selectedOrganization) return

    try {
      setDeleting(selectedOrganization.id)
      const result = await HRService.deleteOrganization(selectedOrganization.id)

      // Remove the organization from the list
      setOrganizations(organizations.filter(org => org.id !== selectedOrganization.id))

      setShowDeleteModal(false)
      setSelectedOrganization(null)

      if (onSuccess) {
        // Show detailed success message if available
        const message = result.message || "Organization deleted successfully!"
        onSuccess(message)
      } else {
        onError("Organization deleted successfully!")
      }
    } catch (error: any) {
      console.error("Error deleting organization:", error)

      let errorMessage = "Failed to delete organization"
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      // Show detailed error with reassignment option
      setOrganizationDependencies({
        agents: selectedOrganization._count?.agents || 0,
        teams: selectedOrganization._count?.teams || 0,
        campaigns: selectedOrganization._count?.campaigns || 0,
        children: selectedOrganization.children?.length || 0
      })
      setShowReassignModal(true)
      setShowDeleteModal(false)
    } finally {
      setDeleting(null)
    }
  }
  
  const handleBulkReassignAgents = async () => {
    if (!selectedOrganization || !reassignToOrgId) return

    try {
      const result = await HRService.bulkReassignAgents(selectedOrganization.id, reassignToOrgId)

      // Refresh organizations to get updated data
      await loadOrganizations()

      setShowReassignModal(false)
      setSelectedOrganization(null)
      setReassignToOrgId("")

      if (onSuccess) {
        onSuccess(`Successfully reassigned ${result.data?.reassignedCount || 0} agents!`)
      }
    } catch (error: any) {
      console.error("Error reassigning agents:", error)

      let errorMessage = "Failed to reassign agents"
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      onError(errorMessage)
    }
  }

  // Filter organizations (backend already sorts by createdAt DESC - latest first)
  const filteredOrganizations = (Array.isArray(organizations) ? organizations : [])
    .filter(org => {
      // Enhanced search - search across organization name, type, and all hierarchy levels
      const searchLower = searchTerm.toLowerCase()
      const matchesSearch = searchTerm === "" || (
        org.name.toLowerCase().includes(searchLower) ||
        org.type.toLowerCase().includes(searchLower) ||
        (org.emLevel && org.emLevel !== 'N/A' && org.emLevel.toLowerCase().includes(searchLower)) ||
        (org.dmLevel && org.dmLevel !== 'N/A' && org.dmLevel.toLowerCase().includes(searchLower)) ||
        (org.cmLevel && org.cmLevel !== 'N/A' && org.cmLevel.toLowerCase().includes(searchLower)) ||
        (org.bmLevel && org.bmLevel !== 'N/A' && org.bmLevel.toLowerCase().includes(searchLower)) ||
        (org.amLevel && org.amLevel !== 'N/A' && org.amLevel.toLowerCase().includes(searchLower))
      )
      
      const matchesType = typeFilter === "all" || org.type === typeFilter
      
      // Hierarchy level filters
      const matchesEm = emFilter === "all" || (org.emLevel === emFilter || (emFilter === "none" && (!org.emLevel || org.emLevel === 'N/A')))
      const matchesDm = dmFilter === "all" || (org.dmLevel === dmFilter || (dmFilter === "none" && (!org.dmLevel || org.dmLevel === 'N/A')))
      const matchesCm = cmFilter === "all" || (org.cmLevel === cmFilter || (cmFilter === "none" && (!org.cmLevel || org.cmLevel === 'N/A')))
      const matchesBm = bmFilter === "all" || (org.bmLevel === bmFilter || (bmFilter === "none" && (!org.bmLevel || org.bmLevel === 'N/A')))
      const matchesAm = amFilter === "all" || (org.amLevel === amFilter || (amFilter === "none" && (!org.amLevel || org.amLevel === 'N/A')))
      
      return matchesSearch && matchesType && matchesEm && matchesDm && matchesCm && matchesBm && matchesAm
    })
    .sort((a, b) => {
      let aValue: any = a[sortField]
      let bValue: any = b[sortField]

      // Check if values are null/undefined/N/A
      const aIsEmpty = aValue === null || aValue === undefined || aValue === 'N/A' || aValue === ''
      const bIsEmpty = bValue === null || bValue === undefined || bValue === 'N/A' || bValue === ''

      // If both are empty, they're equal
      if (aIsEmpty && bIsEmpty) return 0

      // If only a is empty, put it at the end
      if (aIsEmpty) return 1

      // If only b is empty, put it at the end
      if (bIsEmpty) return -1

      // Convert to string for comparison
      const aStr = String(aValue).toLowerCase()
      const bStr = String(bValue).toLowerCase()

      // Sort based on direction
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr)
      } else {
        return bStr.localeCompare(aStr)
      }
    })

  // Pagination calculations
  const totalItems = filteredOrganizations.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value)
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setSearchTerm("")
    setTypeFilter("all")
    setEmFilter("all")
    setDmFilter("all")
    setCmFilter("all")
    setBmFilter("all")
    setAmFilter("all")
    setCurrentPage(1)
  }

  // Load list IDs for organization assignment
  const loadListIds = async () => {
    try {
      const listIdsData = await (HRService as any).getAllListIds()

      // Check if the specific ListId exists
      const targetListId = 'cmg90a9ly0001sixqr6xav239'
      const hasTargetListId = listIdsData.some((lid: any) => lid.id === targetListId)

      // Ensure UNASSIGNED is always present
      const hasUnassigned = listIdsData.some((lid: any) => lid.name === 'UNASSIGNED')
      if (!hasUnassigned) {
        listIdsData.push({
          id: 'unassigned',
          name: 'UNASSIGNED',
          description: 'Unassigned organizations',
          color: '#6B7280',
          isSystem: true,
          isActive: true,
          createdAt: new Date().toISOString()
        })
      }
      setListIds(listIdsData)
    } catch (error) {
      console.error("Error loading list IDs:", error)
      setListIds([{
        id: 'unassigned',
        name: 'UNASSIGNED',
        isActive: true,
        createdAt: new Date().toISOString(),
        color: '#6B7280'
      }])
    }
  }

  // Organization List ID Management Functions
  const loadListIdStats = async () => {
    try {
      setLoadingListIds(true)
      // Backend now already filters EM organizations, so we can use the stats directly
      const stats = await (HRService as any).getOrganizationListIdStats()
      setListIdStats(Array.isArray(stats) ? stats : [])
    } catch (error) {
      onError("Failed to load list ID statistics")
      console.error("Error loading list ID stats:", error)
      setListIdStats([]) // Set empty array on error
    } finally {
      setLoadingListIds(false)
    }
  }

  const loadOrganizationsByListId = async () => {
    try {
      // Get list IDs from the loaded list IDs, ensuring UNASSIGNED is always included
      const listIdData = listIds.length > 0 ? listIds : [{ id: 'unassigned', name: 'UNASSIGNED' }]
      const organizationsByList: { [key: string]: Organization[] } = {}
  
      for (const listId of listIdData) {
        try {
          // For UNASSIGNED, pass 'unassigned', otherwise pass the ListId ID
          const listIdParam = listId.name === 'UNASSIGNED' ? 'unassigned' : listId.id
          const response = await (HRService as any).getOrganizationsByListId(listIdParam)
          // Backend now already filters EM organizations, so use data directly
          organizationsByList[listId.name] = Array.isArray(response.data) ? response.data : []
        } catch (listError) {
          console.error(`Error loading organizations for list ${listId.name}:`, listError)
          organizationsByList[listId.name] = [] // Set empty array for this list
        }
      }
  
      setOrganizationsByListId(organizationsByList)
    } catch (error) {
      onError("Failed to load organizations by list ID")
      console.error("Error loading organizations by list ID:", error)
      // Initialize with empty arrays for UNASSIGNED at minimum
      setOrganizationsByListId({
        'UNASSIGNED': []
      })
    }
  }

  const updateOrganizationListId = async (organizationId: string, newListId: string) => {
    try {
      setLoadingListIds(true) // Show shimmer effects

      // Update the organization in the database
      await (HRService as any).updateOrganizationListId(organizationId, newListId)

      // Reload all data from backend to ensure consistency
      await Promise.all([
        loadListIdStats(),
        loadOrganizationsByListId(),
        loadOrganizations() // Reload main organizations list from backend
      ])

      // Show success feedback
      setSuccessfulUpdate(organizationId)
      setTimeout(() => setSuccessfulUpdate(null), 2000)

      if (onSuccess) {
        onSuccess("Organization ID updated successfully!")
      }
    } catch (error) {
      onError("Failed to update organization ID")
      console.error("Error updating organization ID:", error)
    } finally {
      setLoadingListIds(false) // Hide shimmer effects
    }
  }

  const handleDragStart = (e: React.DragEvent, organization: Organization) => {
    setDraggedOrganization(organization)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', organization.id)

    // Add visual feedback
    const target = e.target as HTMLElement
    target.style.opacity = '0.5'
    target.style.transform = 'scale(0.95)'
    target.style.transition = 'all 0.2s ease'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    // Add visual feedback to drop zone
    const target = e.currentTarget as HTMLElement
    target.style.transform = 'scale(1.05)'
    target.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.15)'
    target.style.transition = 'all 0.2s ease'
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Reset drop zone styling
    const target = e.currentTarget as HTMLElement
    target.style.transform = 'scale(1)'
    target.style.boxShadow = ''
  }

  const handleDrop = async (e: React.DragEvent, targetListId: string) => {
    e.preventDefault()

    // Reset drop zone styling
    const target = e.currentTarget as HTMLElement
    target.style.transform = 'scale(1)'
    target.style.boxShadow = ''

    if (!draggedOrganization) return

    // Don't update if already in the same list
    const currentListIdName = getListIdName((draggedOrganization as any).listId)
    if (currentListIdName === targetListId) {
      setDraggedOrganization(null)
      return
    }

    // Add visual feedback for successful drop
    target.style.transform = 'scale(1.02)'
    target.style.backgroundColor = 'rgba(34, 197, 94, 0.1)'
    target.style.borderColor = 'rgb(34, 197, 94)'

    await updateOrganizationListId(draggedOrganization.id, targetListId)
    setDraggedOrganization(null)

    // Reset styling after a short delay
    setTimeout(() => {
      target.style.transform = 'scale(1)'
      target.style.backgroundColor = ''
      target.style.borderColor = ''
    }, 500)
  }

  // Add drag end handler to reset visual feedback
  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.target as HTMLElement
    target.style.opacity = '1'
    target.style.transform = 'scale(1)'
    target.style.transition = ''
    setDraggedOrganization(null)
  }

  // Helper function to get ListId name from ID
  const getListIdName = (listIdId: string | null) => {
    if (!listIdId) return 'UNASSIGNED'
    const listIdData = listIds.find(lid => lid.id === listIdId)

    if (!listIdData) {
      console.warn(`ListId with ID ${listIdId} not found in loaded ListIds. This might indicate a data loading issue.`)
      // Return a fallback that indicates the ID exists but name is unknown
      return `UNKNOWN (${listIdId.substring(0, 8)}...)`
    }

    return listIdData.name
  }

  const getListIdColor = (listIdName: string) => {
    // Find the list ID in our data to get its actual color
    const listIdData = listIds.find(lid => lid.name === listIdName)
    if (listIdData && listIdData.color) {
      // Use the actual color from the database
      return {
        backgroundColor: `${listIdData.color}20`, // 20% opacity
        borderColor: listIdData.color,
        textColor: listIdData.color
      }
    }

    // Fallback to default colors if not found
    const fallbackColors = {
      ELITE: { backgroundColor: '#8B5CF620', borderColor: '#8B5CF6', textColor: '#8B5CF6' },
      PRIME: { backgroundColor: '#3B82F620', borderColor: '#3B82F6', textColor: '#3B82F6' },
      WIN: { backgroundColor: '#10B98120', borderColor: '#10B981', textColor: '#10B981' },
      SUN: { backgroundColor: '#F59E0B20', borderColor: '#F59E0B', textColor: '#F59E0B' },
      UNASSIGNED: { backgroundColor: '#6B728020', borderColor: '#6B7280', textColor: '#6B7280' },
    }
    return fallbackColors[listIdName as keyof typeof fallbackColors] || fallbackColors.UNASSIGNED
  }

  // Get unique values for each hierarchy level for filter dropdowns with cascading filters
  const getUniqueHierarchyValues = (level: 'emLevel' | 'dmLevel' | 'cmLevel' | 'bmLevel' | 'amLevel') => {
    let filteredOrgs = organizations

    // Apply cascading filters based on the level being requested
    if (level === 'dmLevel' && emFilter !== 'all') {
      // Filter DM values by selected EM
      filteredOrgs = filteredOrgs.filter(org =>
        emFilter === 'none'
          ? (!org.emLevel || org.emLevel === 'N/A')
          : org.emLevel === emFilter
      )
    } else if (level === 'cmLevel') {
      // Filter CM values by selected EM and DM
      if (emFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          emFilter === 'none'
            ? (!org.emLevel || org.emLevel === 'N/A')
            : org.emLevel === emFilter
        )
      }
      if (dmFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          dmFilter === 'none'
            ? (!org.dmLevel || org.dmLevel === 'N/A')
            : org.dmLevel === dmFilter
        )
      }
    } else if (level === 'bmLevel') {
      // Filter BM values by selected EM, DM, and CM
      if (emFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          emFilter === 'none'
            ? (!org.emLevel || org.emLevel === 'N/A')
            : org.emLevel === emFilter
        )
      }
      if (dmFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          dmFilter === 'none'
            ? (!org.dmLevel || org.dmLevel === 'N/A')
            : org.dmLevel === dmFilter
        )
      }
      if (cmFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          cmFilter === 'none'
            ? (!org.cmLevel || org.cmLevel === 'N/A')
            : org.cmLevel === cmFilter
        )
      }
    } else if (level === 'amLevel') {
      // Filter AM values by selected EM, DM, CM, and BM
      if (emFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          emFilter === 'none'
            ? (!org.emLevel || org.emLevel === 'N/A')
            : org.emLevel === emFilter
        )
      }
      if (dmFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          dmFilter === 'none'
            ? (!org.dmLevel || org.dmLevel === 'N/A')
            : org.dmLevel === dmFilter
        )
      }
      if (cmFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          cmFilter === 'none'
            ? (!org.cmLevel || org.cmLevel === 'N/A')
            : org.cmLevel === cmFilter
        )
      }
      if (bmFilter !== 'all') {
        filteredOrgs = filteredOrgs.filter(org =>
          bmFilter === 'none'
            ? (!org.bmLevel || org.bmLevel === 'N/A')
            : org.bmLevel === bmFilter
        )
      }
    }

    const values = filteredOrgs
      .map(org => org[level])
      .filter(value => value && value !== 'N/A')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort()
    return values
  }


  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-accent-900">Organization Management</h1>
          <p className="text-accent-600">Create and manage organizational hierarchy</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
        >
          <Plus className="w-4 h-4" />
          <span>Create Organization</span>
        </button>
      </div>

      {/* Error Display */}
      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Unable to load organizations</h3>
              <p className="text-sm text-red-700 mt-1">
                There was an error loading the organizations data. Please check your connection and try again.
              </p>
            </div>
            <button
              onClick={loadOrganizations}
              className="ml-4 px-3 py-1 text-sm bg-red-100 text-red-800 rounded-md hover:bg-red-200 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={typeFilter}
              onChange={(e) => handleTypeFilterChange(e.target.value)}
              className="input-field"
            >
              <option value="all">All Types</option>
              {availableTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          {/* FMO Only - Organization ID Assignment */}
          {user?.role === 'fmo_ceo' && (
            <button
              onClick={() => {
                setShowListIdModal(true)
                // useEffect will handle loading fresh data from backend
              }}
              className="btn-secondary flex items-center space-x-2"
            >
              <Tag className="w-4 h-4" />
              <span>Organization ID Assignment</span>
            </button>
          )}

          {/* Clear All Filters Button */}
          <button
            onClick={clearAllFilters}
            className="btn-outline flex items-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
          >
            <X className="w-4 h-4" />
            <span>Clear Filters</span>
          </button>

        </div>


      </div>


      {/* Organizations Table - Improved Design */}
      <div className="card overflow-hidden">
        {loading ? (
          <OrganizationShimmerTable rows={10} />
        ) : (
          <>
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full min-w-full divide-y divide-accent-200">
                <thead className="bg-gradient-to-r from-accent-50 to-accent-100">
                <tr>
                  <th 
                      className="px-4 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider cursor-pointer hover:bg-accent-200 transition-colors min-w-[200px]"
                      onClick={() => handleSort('name' as keyof Organization)}
                    >
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-primary-600" />
                        <span>Organization</span>
                        {getSortIcon('name' as keyof Organization)}
                    </div>
                  </th>
                  
                  {/* Hierarchy Level Columns with Filters */}
                  <th 
                    className="px-3 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-accent-200 transition-colors"
                    onClick={() => handleSort('emLevel')}
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                        <span>EM Level</span>
                        {getSortIcon('emLevel')}
                      </div>
                      <select
                        value={emFilter}
                        onChange={(e) => {
                          setEmFilter(e.target.value)
                          // Reset child filters when parent changes
                          setDmFilter('all')
                          setCmFilter('all')
                          setBmFilter('all')
                          setAmFilter('all')
                          setCurrentPage(1)
                        }}
                        className="text-xs border border-accent-300 rounded px-2 py-1 bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="all">All EM</option>
                        <option value="none">No EM</option>
                        {getUniqueHierarchyValues('emLevel').map(value => (
                          <option key={value} value={value || ''}>{value}</option>
                        ))}
                      </select>
                    </div>
                  </th>

                  <th 
                    className="px-3 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-accent-200 transition-colors"
                    onClick={() => handleSort('dmLevel')}
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-green-600"></div>
                        <span>DM Level</span>
                        {getSortIcon('dmLevel')}
                      </div>
                      <select
                        value={dmFilter}
                        onChange={(e) => {
                          setDmFilter(e.target.value)
                          // Reset child filters when parent changes
                          setCmFilter('all')
                          setBmFilter('all')
                          setAmFilter('all')
                          setCurrentPage(1)
                        }}
                        className="text-xs border border-accent-300 rounded px-2 py-1 bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="all">All DM</option>
                        <option value="none">No DM</option>
                        {getUniqueHierarchyValues('dmLevel').map(value => (
                          <option key={value} value={value || ''}>{value}</option>
                        ))}
                      </select>
                    </div>
                  </th>

                  <th 
                    className="px-3 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-accent-200 transition-colors"
                    onClick={() => handleSort('cmLevel')}
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-600"></div>
                        <span>CM Level</span>
                        {getSortIcon('cmLevel')}
                      </div>
                      <select
                        value={cmFilter}
                        onChange={(e) => {
                          setCmFilter(e.target.value)
                          // Reset child filters when parent changes
                          setBmFilter('all')
                          setAmFilter('all')
                          setCurrentPage(1)
                        }}
                        className="text-xs border border-accent-300 rounded px-2 py-1 bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="all">All CM</option>
                        <option value="none">No CM</option>
                        {getUniqueHierarchyValues('cmLevel').map(value => (
                          <option key={value} value={value || ''}>{value}</option>
                        ))}
                      </select>
                    </div>
                  </th>

                  <th 
                    className="px-3 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-accent-200 transition-colors"
                    onClick={() => handleSort('bmLevel')}
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-orange-600"></div>
                        <span>BM Level</span>
                        {getSortIcon('bmLevel')}
                      </div>
                      <select
                        value={bmFilter}
                        onChange={(e) => {
                          setBmFilter(e.target.value)
                          // Reset child filter when parent changes
                          setAmFilter('all')
                          setCurrentPage(1)
                        }}
                        className="text-xs border border-accent-300 rounded px-2 py-1 bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="all">All BM</option>
                        <option value="none">No BM</option>
                        {getUniqueHierarchyValues('bmLevel').map(value => (
                          <option key={value} value={value || ''}>{value}</option>
                        ))}
                      </select>
                    </div>
                  </th>

                  <th 
                    className="px-3 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-accent-200 transition-colors"
                    onClick={() => handleSort('amLevel')}
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-red-600"></div>
                        <span>AM Level</span>
                        {getSortIcon('amLevel')}
                      </div>
                      <select
                        value={amFilter}
                        onChange={(e) => {
                          setAmFilter(e.target.value)
                          setCurrentPage(1)
                        }}
                        className="text-xs border border-accent-300 rounded px-2 py-1 bg-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="all">All AM</option>
                        <option value="none">No AM</option>
                        {getUniqueHierarchyValues('amLevel').map(value => (
                          <option key={value} value={value || ''}>{value}</option>
                        ))}
                      </select>
                    </div>
                  </th>

                  <th 
                      className="px-4 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider cursor-pointer hover:bg-accent-200 transition-colors min-w-[100px]"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span>Created</span>
                        {sortField === 'createdAt' && getSortIcon('createdAt')}
                    </div>
                  </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[100px]">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Status</span>
                    </div>
                  </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[100px]">
                      <div className="flex items-center space-x-2">
                        <Tag className="w-4 h-4 text-indigo-600" />
                        <span>List ID</span>
                    </div>
                  </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-accent-700 uppercase tracking-wider min-w-[120px]">
                    Actions
                  </th>
                </tr>
              </thead>
                <tbody className="bg-white divide-y divide-accent-100">
                  {paginatedOrganizations.map((org, index) => (
                    <tr 
                      key={org.id} 
                      className={`
                        transition-all duration-150 hover:bg-accent-50 hover:shadow-sm
                        ${updating === org.id ? 'bg-blue-50 opacity-75' : ''}
                        ${index % 2 === 0 ? 'bg-white' : 'bg-accent-25'}
                      `}
                    >
                      {/* Organization Name & Type Column */}
                    <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                        {updating === org.id ? (
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 animate-pulse">
                              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          </div>
                        ) : (
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm ${
                              org.type === 'FMO' ? 'bg-gradient-to-br from-indigo-500 to-indigo-700' :
                              org.type === 'EM' ? 'bg-gradient-to-br from-purple-500 to-purple-700' :
                              org.type === 'DM' ? 'bg-gradient-to-br from-green-500 to-green-700' :
                              org.type === 'CM' ? 'bg-gradient-to-br from-yellow-500 to-yellow-700' :
                              org.type === 'BM' ? 'bg-gradient-to-br from-orange-500 to-orange-700' :
                              org.type === 'AM' ? 'bg-gradient-to-br from-red-500 to-red-700' :
                              'bg-gradient-to-br from-gray-500 to-gray-700'
                            }`}>
                              {org.type.charAt(0)}
                          </div>
                        )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-accent-900 truncate">
                              {org.name}
                            </p>
                            <p className="text-xs text-accent-500 mt-0.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getOrganizationTypeBadge(org.type)}`}>
                                {org.type}
                              </span>
                            </p>
                        </div>
                      </div>
                    </td>

                      {/* EM Level Column */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {org.emLevel && org.emLevel !== 'N/A' ? (
                          <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-purple-600 mr-1.5"></span>
                                {org.emLevel}
                              </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">-</span>
                        )}
                      </td>

                      {/* DM Level Column */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {org.dmLevel && org.dmLevel !== 'N/A' ? (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-600 mr-1.5"></span>
                                {org.dmLevel}
                              </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">-</span>
                        )}
                      </td>

                      {/* CM Level Column */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {org.cmLevel && org.cmLevel !== 'N/A' ? (
                          <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-600 mr-1.5"></span>
                                {org.cmLevel}
                              </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">-</span>
                        )}
                      </td>

                      {/* BM Level Column */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {org.bmLevel && org.bmLevel !== 'N/A' ? (
                          <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-600 mr-1.5"></span>
                                {org.bmLevel}
                              </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">-</span>
                        )}
                      </td>

                      {/* AM Level Column */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {org.amLevel && org.amLevel !== 'N/A' ? (
                          <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600 mr-1.5"></span>
                              {org.amLevel}
                            </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">-</span>
                        )}
                    </td>

                      {/* Created Date Column */}
                    <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-accent-700 font-medium">
                            {new Date(org.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                        </div>
                      </div>
                    </td>

                      {/* Status Column */}
                    <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
                          org.isActive
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            org.isActive ? 'bg-green-500' : 'bg-red-500'
                          }`}></span>
                        {org.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* List ID Column */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm"
                        style={(() => {
                          const listIdName = (org as any).listIdName || 'UNASSIGNED'
                          const colors = getListIdColor(listIdName)
                          return {
                            backgroundColor: colors.backgroundColor,
                            color: colors.textColor,
                              border: `1.5px solid ${colors.borderColor}`
                          }
                        })()}
                      >
                          <Tag className="w-3 h-3 mr-1.5" />
                        {(org as any).listIdName || 'UNASSIGNED'}
                      </span>
                    </td>

                    {/* Actions Column */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleViewOrganization(org)}
                            className="p-2 text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-lg transition-all"
                          title="View details"
                          disabled={loadingViewDetails === org.id}
                        >
                          {loadingViewDetails === org.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrganization(org)
                            setShowDeleteModal(true)
                          }}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete organization"
                          disabled={deleting === org.id}
                        >
                          {deleting === org.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

            {filteredOrganizations.length === 0 && (
              <div className="text-center py-12 bg-accent-25">
                <Building2 className="w-16 h-16 text-accent-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-accent-600">No organizations found</p>
                <p className="text-sm text-accent-500 mt-2">Try adjusting your search or filters</p>
          </div>
            )}
          </>
        )}

        {/* Pagination Controls */}
        {!loading && filteredOrganizations.length > 0 && (
          <div className="border-t border-accent-200 px-6 py-4 flex items-center justify-between bg-accent-25">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-accent-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{" "}
                <span className="font-medium">{totalItems}</span> results
              </span>
            </div>

            <div className="flex items-center space-x-2">
              {/* Items per page selector */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-accent-700">Per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="input-field py-1 px-2 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {/* Page navigation */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md text-sm font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  <span className="sr-only">First</span>
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md text-sm font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <span className="sr-only">Previous</span>
                  ‹
                </button>

                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${currentPage === pageNum
                            ? "bg-primary-600 text-white"
                            : "text-accent-700 hover:bg-accent-100"
                          }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md text-sm font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <span className="sr-only">Next</span>
                  ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md text-sm font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  <span className="sr-only">Last</span>
                  »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Header - Sticky */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 flex items-center justify-between rounded-t-xl flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Create New Organization</h2>
                  <p className="text-primary-100 text-sm">Set up a new organization in your hierarchy</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                disabled={creating}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleCreateOrganization} className="p-6 space-y-6">
                {/* Basic Information Section */}
                <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                  <h3 className="text-lg font-semibold text-accent-900 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                    Basic Information
                  </h3>

                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className={`input-field ${formErrors.name ? "border-red-500 ring-2 ring-red-200" : "focus:ring-2 focus:ring-primary-200"}`}
                      placeholder="e.g., Sales Division"
                      disabled={creating}
                    />
                    {formErrors.name && (
                      <p className="text-red-600 text-sm mt-1 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-accent-700 mb-2">
                        Organization Type *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => {
                          const newType = e.target.value
                          setFormData(prev => ({ ...prev, type: newType, parentId: undefined }))
                          loadParentOrganizations(newType)
                        }}
                        className={`input-field ${formErrors.type ? "border-red-500 ring-2 ring-red-200" : "focus:ring-2 focus:ring-primary-200"}`}
                        disabled={creating}
                      >
                        <option value="">Select type...</option>
                        {availableTypes.map(type => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      {formErrors.type && (
                        <p className="text-red-600 text-sm mt-1 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          {formErrors.type}
                        </p>
                      )}
                    </div>

                    {formData.type && (
                      <div>
                        <label className="block text-sm font-semibold text-accent-700 mb-2">
                          Parent Organization
                        </label>
                        {loadingParents ? (
                          <div className="input-field bg-accent-100 animate-pulse h-[42px] flex items-center">
                            <div className="flex items-center space-x-2">
                              <Loader2 className="w-4 h-4 text-primary-600 animate-spin" />
                              <span className="text-sm text-accent-500">Loading parent organizations...</span>
                            </div>
                          </div>
                        ) : formData.type === 'EM' || formData.type === 'FMO' ? (
                          <div className="input-field bg-accent-50 text-accent-500 cursor-not-allowed">
                            {formData.type} is the top level - no parent required
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* DM Selection - Only needs EM */}
                            {formData.type === 'DM' && (
                              <div>
                                <label className="block text-xs font-medium text-accent-600 mb-1">
                                  Select EM Level Organization
                                </label>
                                <select
                                  value={formData.parentId || ""}
                                  onChange={(e) => {
                                    handleInputChange("parentId", e.target.value)
                                    setSelectedEMParent(e.target.value)
                                  }}
                                  className="input-field focus:ring-2 focus:ring-primary-200"
                                  disabled={creating}
                                >
                                  <option value="">Choose EM organization...</option>
                                  {availableEMOrgs.map(org => (
                                    <option key={org.id} value={org.id}>
                                      {org.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* CM Selection - Needs EM → DM */}
                            {formData.type === 'CM' && (
                              <>
                                {/* Step 1: Select EM */}
                                <div>
                                  <label className="block text-xs font-medium text-accent-600 mb-1">
                                    1. Select EM Level Organization
                                  </label>
                                  <select
                                    value={selectedEMParent}
                                    onChange={(e) => {
                                      setSelectedEMParent(e.target.value)
                                      setSelectedDMParent("")
                                      setFormData(prev => ({ ...prev, parentId: undefined }))
                                      loadDMOrganizations(e.target.value)
                                    }}
                                    className="input-field focus:ring-2 focus:ring-primary-200"
                                    disabled={creating}
                                  >
                                    <option value="">Choose EM organization...</option>
                                    {availableEMOrgs.map(org => (
                                      <option key={org.id} value={org.id}>
                                        {org.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Step 2: Select DM (only after EM selected) */}
                                {selectedEMParent && (
                                <div>
                                  <label className="block text-xs font-medium text-accent-600 mb-1">
                                      2. Select DM Level Organization (Immediate Parent)
                                  </label>
                                  <select
                                    value={formData.parentId || ""}
                                      onChange={(e) => {
                                        handleInputChange("parentId", e.target.value)
                                        setSelectedDMParent(e.target.value)
                                      }}
                                    className="input-field focus:ring-2 focus:ring-primary-200"
                                    disabled={creating}
                                  >
                                      <option value="">Choose DM organization...</option>
                                      {availableDMOrgs.map(org => (
                                      <option key={org.id} value={org.id}>
                                        {org.name}
                                      </option>
                                    ))}
                                  </select>
                                  </div>
                                )}
                              </>
                            )}

                            {/* BM Selection - Needs EM → DM → CM */}
                            {formData.type === 'BM' && (
                              <>
                                {/* Step 1: Select EM */}
                                <div>
                                  <label className="block text-xs font-medium text-accent-600 mb-1">
                                    1. Select EM Level Organization
                                  </label>
                                  <select
                                    value={selectedEMParent}
                                    onChange={(e) => {
                                      setSelectedEMParent(e.target.value)
                                      setSelectedDMParent("")
                                      setSelectedCMParent("")
                                      setFormData(prev => ({ ...prev, parentId: undefined }))
                                      loadDMOrganizations(e.target.value)
                                    }}
                                    className="input-field focus:ring-2 focus:ring-primary-200"
                                    disabled={creating}
                                  >
                                    <option value="">Choose EM organization...</option>
                                    {availableEMOrgs.map(org => (
                                      <option key={org.id} value={org.id}>
                                        {org.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Step 2: Select DM */}
                                {selectedEMParent && (
                                  <div>
                                    <label className="block text-xs font-medium text-accent-600 mb-1">
                                      2. Select DM Level Organization
                                    </label>
                                    <select
                                      value={selectedDMParent}
                                      onChange={(e) => {
                                        setSelectedDMParent(e.target.value)
                                        setSelectedCMParent("")
                                        setFormData(prev => ({ ...prev, parentId: undefined }))
                                        loadCMOrganizations(e.target.value)
                                      }}
                                      className="input-field focus:ring-2 focus:ring-primary-200"
                                      disabled={creating}
                                    >
                                      <option value="">Choose DM organization...</option>
                                      {availableDMOrgs.map(org => (
                                        <option key={org.id} value={org.id}>
                                          {org.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {/* Step 3: Select CM (Immediate Parent) */}
                                {selectedDMParent && (
                                  <div>
                                    <label className="block text-xs font-medium text-accent-600 mb-1">
                                      3. Select CM Level Organization (Immediate Parent)
                                    </label>
                                    <select
                                      value={formData.parentId || ""}
                                      onChange={(e) => {
                                        handleInputChange("parentId", e.target.value)
                                        setSelectedCMParent(e.target.value)
                                      }}
                                      className="input-field focus:ring-2 focus:ring-primary-200"
                                      disabled={creating}
                                    >
                                      <option value="">Choose CM organization...</option>
                                      {availableCMOrgs.map(org => (
                                        <option key={org.id} value={org.id}>
                                          {org.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </>
                            )}

                            {/* AM Selection - Needs EM → DM → CM → BM */}
                            {formData.type === 'AM' && (
                              <>
                                {/* Step 1: Select EM */}
                                <div>
                                  <label className="block text-xs font-medium text-accent-600 mb-1">
                                    1. Select EM Level Organization
                                  </label>
                                  <select
                                    value={selectedEMParent}
                                    onChange={(e) => {
                                      setSelectedEMParent(e.target.value)
                                      setSelectedDMParent("")
                                      setSelectedCMParent("")
                                      setSelectedBMParent("")
                                      setFormData(prev => ({ ...prev, parentId: undefined }))
                                      loadDMOrganizations(e.target.value)
                                    }}
                                    className="input-field focus:ring-2 focus:ring-primary-200"
                                    disabled={creating}
                                  >
                                    <option value="">Choose EM organization...</option>
                                    {availableEMOrgs.map(org => (
                                      <option key={org.id} value={org.id}>
                                        {org.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Step 2: Select DM */}
                                {selectedEMParent && (
                                  <div>
                                    <label className="block text-xs font-medium text-accent-600 mb-1">
                                      2. Select DM Level Organization
                                    </label>
                                    <select
                                      value={selectedDMParent}
                                      onChange={(e) => {
                                        setSelectedDMParent(e.target.value)
                                        setSelectedCMParent("")
                                        setSelectedBMParent("")
                                        setFormData(prev => ({ ...prev, parentId: undefined }))
                                        loadCMOrganizations(e.target.value)
                                      }}
                                      className="input-field focus:ring-2 focus:ring-primary-200"
                                      disabled={creating}
                                    >
                                      <option value="">Choose DM organization...</option>
                                      {availableDMOrgs.map(org => (
                                        <option key={org.id} value={org.id}>
                                          {org.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {/* Step 3: Select CM */}
                                {selectedDMParent && (
                                  <div>
                                    <label className="block text-xs font-medium text-accent-600 mb-1">
                                      3. Select CM Level Organization
                                    </label>
                                    <select
                                      value={selectedCMParent}
                                      onChange={(e) => {
                                        setSelectedCMParent(e.target.value)
                                        setSelectedBMParent("")
                                        setFormData(prev => ({ ...prev, parentId: undefined }))
                                        loadBMOrganizations(e.target.value)
                                      }}
                                      className="input-field focus:ring-2 focus:ring-primary-200"
                                      disabled={creating}
                                    >
                                      <option value="">Choose CM organization...</option>
                                      {availableCMOrgs.map(org => (
                                        <option key={org.id} value={org.id}>
                                          {org.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {/* Step 4: Select BM (Immediate Parent) */}
                                {selectedCMParent && (
                                  <div>
                                    <label className="block text-xs font-medium text-accent-600 mb-1">
                                      4. Select BM Level Organization (Immediate Parent)
                                    </label>
                                    <select
                                      value={formData.parentId || ""}
                                      onChange={(e) => {
                                        handleInputChange("parentId", e.target.value)
                                        setSelectedBMParent(e.target.value)
                                      }}
                                      className="input-field focus:ring-2 focus:ring-primary-200"
                                      disabled={creating}
                                    >
                                      <option value="">Choose BM organization...</option>
                                      {availableBMOrgs.map(org => (
                                        <option key={org.id} value={org.id}>
                                          {org.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Hierarchy Path Preview */}
                                  {formData.parentId && (
                                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                      <div className="text-xs font-medium text-blue-800 mb-2">Hierarchy Path:</div>
                                      <div className="text-xs text-blue-700">
                                        {(() => {
                                          const selectedOrg = parentOrganizations.find(org => org.id === formData.parentId)
                                          if (!selectedOrg) return 'Unknown Organization'
                                          
                                          const hierarchyPath = []
                                          
                                    if (selectedOrg.emLevel) hierarchyPath.push(selectedOrg.emLevel)
                                    if (selectedOrg.dmLevel) hierarchyPath.push(selectedOrg.dmLevel)
                                    if (selectedOrg.cmLevel) hierarchyPath.push(selectedOrg.cmLevel)
                                    if (selectedOrg.bmLevel) hierarchyPath.push(selectedOrg.bmLevel)
                                    if (selectedOrg.amLevel) hierarchyPath.push(selectedOrg.amLevel)
                                    
                                          hierarchyPath.push(selectedOrg.name)
                                          
                                          return hierarchyPath.join(' → ')
                                        })()}
                                      </div>
                                    </div>
                                  )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>


                </div>

                {/* Additional Settings Section */}
                <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                  <h3 className="text-lg font-semibold text-accent-900 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                    Additional Details <span className="text-sm font-normal text-accent-500 ml-2">(Optional)</span>
                  </h3>

                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.settings.description}
                      onChange={(e) => handleSettingsChange("description", e.target.value)}
                      className="input-field focus:ring-2 focus:ring-primary-200"
                      rows={3}
                      placeholder="Brief description of this organization..."
                      disabled={creating}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-accent-700 mb-2 flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        Address
                      </label>
                      <input
                        type="text"
                        value={formData.settings.address}
                        onChange={(e) => handleSettingsChange("address", e.target.value)}
                        className="input-field focus:ring-2 focus:ring-primary-200"
                        placeholder="123 Main St, City, State"
                        disabled={creating}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-accent-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.settings.phone}
                        onChange={(e) => handleSettingsChange("phone", e.target.value)}
                        className="input-field focus:ring-2 focus:ring-primary-200"
                        placeholder="(555) 123-4567"
                        disabled={creating}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.settings.email}
                      onChange={(e) => handleSettingsChange("email", e.target.value)}
                      className="input-field focus:ring-2 focus:ring-primary-200"
                      placeholder="contact@organization.com"
                      disabled={creating}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-accent-200">
                  <button
                    type="button"
                    onClick={() => {
                      clearCreateForm()
                      setShowCreateForm(false)
                    }}
                    className="px-5 py-2.5 border border-accent-300 rounded-lg text-accent-700 font-medium hover:bg-accent-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Creating Organization...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        <span>Create Organization</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Organization Modal */}
      {showViewModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Header - Sticky */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 flex items-center justify-between rounded-t-xl flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{isEditing ? "Edit Organization" : "Organization Details"}</h2>
                  <p className="text-primary-100 text-sm">{isEditing ? "Update organization information" : "View organization details"}</p>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleUpdateOrganization} className="p-6 space-y-6">
                {loadingViewDetails !== null ? (
                  // Shimmer effect while loading
                  <div className="space-y-6">
                    <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                      <div className="h-6 bg-accent-200 rounded animate-pulse w-44"></div>
                      <div className="space-y-4">
                        <div className="h-4 bg-accent-200 rounded animate-pulse w-32"></div>
                        <div className="h-10 bg-accent-100 rounded animate-pulse"></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[...Array(2)].map((_, i) => (
                          <div key={i} className="space-y-2">
                            <div className="h-4 bg-accent-200 rounded animate-pulse w-24"></div>
                            <div className="h-10 bg-accent-100 rounded animate-pulse"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                      <div className="h-6 bg-accent-200 rounded animate-pulse w-44"></div>
                      <div className="space-y-4">
                        <div className="h-4 bg-accent-200 rounded animate-pulse w-24"></div>
                        <div className="h-20 bg-accent-100 rounded animate-pulse"></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="space-y-2">
                            <div className="h-4 bg-accent-200 rounded animate-pulse w-20"></div>
                            <div className="h-10 bg-accent-100 rounded animate-pulse"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Basic Information Section */}
                    <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                      <h3 className="text-lg font-semibold text-accent-900 flex items-center">
                        <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                        Basic Information
                      </h3>

                      <div>
                        <label className="block text-sm font-semibold text-accent-700 mb-2">
                          Organization Name *
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editFormData.name}
                            onChange={(e) => handleEditInputChange("name", e.target.value)}
                            className={`input-field ${editFormErrors.name ? "border-red-500 ring-2 ring-red-200" : "focus:ring-2 focus:ring-primary-200"}`}
                            placeholder="e.g., Sales Division"
                            disabled={updating === selectedOrganization.id}
                          />
                        ) : (
                          <p className="text-sm text-accent-900 bg-white p-3 rounded border">{selectedOrganization.name}</p>
                        )}
                        {isEditing && editFormErrors.name && (
                          <p className="text-red-600 text-sm mt-1 flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            {editFormErrors.name}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-accent-700 mb-2">
                            Organization Type *
                          </label>
                          {isEditing ? (
                            <select
                              value={editFormData.type}
                              onChange={(e) => {
                                const newType = e.target.value
                                setEditFormData(prev => ({ ...prev, type: newType, parentId: undefined }))
                                if (newType !== selectedOrganization.type) {
                                  loadParentOrganizations(newType)
                                }
                              }}
                              className={`input-field ${editFormErrors.type ? "border-red-500 ring-2 ring-red-200" : "focus:ring-2 focus:ring-primary-200"}`}
                              disabled={updating === selectedOrganization.id}
                            >
                              <option value="">Select type...</option>
                              {availableTypes.map(type => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOrganizationTypeBadge(selectedOrganization.type)}`}>
                              {selectedOrganization.type}
                            </span>
                          )}
                          {isEditing && editFormErrors.type && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              {editFormErrors.type}
                            </p>
                          )}
                        </div>

                        {editFormData.type && (
                          <div>
                            <label className="block text-sm font-semibold text-accent-700 mb-2">
                              Parent Organization
                            </label>
                            {isEditing ? (
                              loadingParents ? (
                                <div className="input-field bg-accent-100 animate-pulse h-[42px] flex items-center">
                                  <div className="flex items-center space-x-2">
                                    <Loader2 className="w-4 h-4 text-primary-600 animate-spin" />
                                    <span className="text-sm text-accent-500">Loading parent organizations...</span>
                                  </div>
                                </div>
                              ) : editFormData.type === 'EM' || editFormData.type === 'FMO' ? (
                                <div className="input-field bg-accent-50 text-accent-500 cursor-not-allowed">
                                  {editFormData.type} is the top level - no parent required
                                </div>
                              ) : parentOrganizations.length > 0 ? (
                                <div className="space-y-3">
                                  {/* Show immediate parent selection only */}
                                  {(() => {
                                    const immediateParentLevel = getNextLevelToShowEdit(editFormData.type)
                                    if (!immediateParentLevel) return null
                                    
                                    const levelOrgs = parentOrganizations.filter(org => org.type === immediateParentLevel)
                                    if (levelOrgs.length === 0) return null
                                    
                                    return (
                                      <div>
                                        <label className="block text-xs font-medium text-accent-600 mb-1">
                                          Select {immediateParentLevel} Level Organization (Immediate Parent)
                                        </label>
                                        <select
                                          value={editFormData.parentId || ""}
                                          onChange={(e) => {
                                            setEditFormData(prev => ({ ...prev, parentId: e.target.value || undefined }))
                                          }}
                                          className="input-field focus:ring-2 focus:ring-primary-200"
                                          disabled={updating === selectedOrganization.id}
                                        >
                                          <option value="">Choose {immediateParentLevel} organization...</option>
                                          {levelOrgs.map(org => (
                                            <option key={org.id} value={org.id}>
                                              {org.name}
                                            </option>
                                          ))}
                                        </select>
                                        
                                        {/* Show hierarchy path */}
                                        {editFormData.parentId && (
                                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <div className="text-xs font-medium text-blue-800 mb-2">Hierarchy Path:</div>
                                            <div className="text-xs text-blue-700">
                                              {(() => {
                                                const selectedOrg = parentOrganizations.find(org => org.id === editFormData.parentId)
                                                if (!selectedOrg) return ''
                                                
                                                // Build hierarchy path using the hierarchy data from backend
                                                const hierarchyPath = []
                                                
                                                // Add EM level if exists
                                                if (selectedOrg.emLevel) {
                                                  hierarchyPath.push(selectedOrg.emLevel)
                                                }
                                                
                                                // Add DM level if exists
                                                if (selectedOrg.dmLevel) {
                                                  hierarchyPath.push(selectedOrg.dmLevel)
                                                }
                                                
                                                // Add CM level if exists
                                                if (selectedOrg.cmLevel) {
                                                  hierarchyPath.push(selectedOrg.cmLevel)
                                                }
                                                
                                                // Add BM level if exists
                                                if (selectedOrg.bmLevel) {
                                                  hierarchyPath.push(selectedOrg.bmLevel)
                                                }
                                                
                                                // Add AM level if exists
                                                if (selectedOrg.amLevel) {
                                                  hierarchyPath.push(selectedOrg.amLevel)
                                                }
                                                
                                                // Add the selected organization itself
                                                hierarchyPath.push(selectedOrg.name)
                                                
                                                return hierarchyPath.join(' → ')
                                              })()}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })()}
                                </div>
                              ) : (
                                <div className="input-field bg-accent-50 text-accent-500 cursor-not-allowed">
                                  No parent organizations available
                                </div>
                              )
                            ) : (
                              <p className="text-sm text-accent-900 bg-white p-3 rounded border">
                                {selectedOrganization.parent?.name || "None (Top Level)"}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Details Section */}
                    <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                      <h3 className="text-lg font-semibold text-accent-900 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                        Additional Details <span className="text-sm font-normal text-accent-500 ml-2">(Optional)</span>
                      </h3>

                      <div>
                        <label className="block text-sm font-semibold text-accent-700 mb-2">
                          Description
                        </label>
                        {isEditing ? (
                          <textarea
                            value={editFormData.settings.description}
                            onChange={(e) => handleEditSettingsChange("description", e.target.value)}
                            className="input-field focus:ring-2 focus:ring-primary-200"
                            rows={3}
                            placeholder="Brief description of this organization..."
                            disabled={updating === selectedOrganization.id}
                          />
                        ) : (
                          <p className="text-sm text-accent-900 bg-white p-3 rounded border">
                            {selectedOrganization.settings?.description || "No description provided"}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-accent-700 mb-2 flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            Address
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editFormData.settings.address}
                              onChange={(e) => handleEditSettingsChange("address", e.target.value)}
                              className="input-field focus:ring-2 focus:ring-primary-200"
                              placeholder="123 Main St, City, State"
                              disabled={updating === selectedOrganization.id}
                            />
                          ) : (
                            <p className="text-sm text-accent-900 bg-white p-3 rounded border">
                              {selectedOrganization.settings?.address || "No address provided"}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-accent-700 mb-2">
                            Phone Number
                          </label>
                          {isEditing ? (
                            <input
                              type="tel"
                              value={editFormData.settings.phone}
                              onChange={(e) => handleEditSettingsChange("phone", e.target.value)}
                              className="input-field focus:ring-2 focus:ring-primary-200"
                              placeholder="(555) 123-4567"
                              disabled={updating === selectedOrganization.id}
                            />
                          ) : (
                            <p className="text-sm text-accent-900 bg-white p-3 rounded border">
                              {selectedOrganization.settings?.phone || "No phone provided"}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-accent-700 mb-2">
                          Email Address
                        </label>
                        {isEditing ? (
                          <input
                            type="email"
                            value={editFormData.settings.email}
                            onChange={(e) => handleEditSettingsChange("email", e.target.value)}
                            className="input-field focus:ring-2 focus:ring-primary-200"
                            placeholder="contact@organization.com"
                            disabled={updating === selectedOrganization.id}
                          />
                        ) : (
                          <p className="text-sm text-accent-900 bg-white p-3 rounded border">
                            {selectedOrganization.settings?.email || "No email provided"}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-accent-200">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setIsEditing(false)
                          // Reset edit form data to original values
                          setEditFormData({
                            name: selectedOrganization.name,
                            type: selectedOrganization.type,
                            parentId: selectedOrganization.parentId || undefined,
                            settings: selectedOrganization.settings || {
                              description: "",
                              address: "",
                              phone: "",
                              email: "",
                            },
                          })
                          setEditFormErrors({})
                        }}
                        disabled={updating === selectedOrganization.id}
                        className="px-5 py-2.5 border border-accent-300 rounded-lg text-accent-700 font-medium hover:bg-accent-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={updating === selectedOrganization.id}
                        className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updating === selectedOrganization.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>Update Organization</span>
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setShowViewModal(false)
                        }}
                        className="px-5 py-2.5 border border-accent-300 rounded-lg text-accent-700 font-medium hover:bg-accent-50 transition-colors"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setIsEditing(true)
                          // Ensure edit form data is properly set with current organization data
                          setEditFormData({
                            name: selectedOrganization.name,
                            type: selectedOrganization.type,
                            parentId: selectedOrganization.parentId || undefined,
                            settings: selectedOrganization.settings || {
                              description: "",
                              address: "",
                              phone: "",
                              email: "",
                            },
                          })
                          // Load parent organizations for the current type
                          loadParentOrganizations(selectedOrganization.type)
                        }}
                        className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg flex items-center space-x-2"
                        disabled={loadingViewDetails !== null}
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}      {showEditModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-5 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <Edit className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Edit Organization</h2>
                  <p className="text-green-100 text-sm">Update organization information and settings</p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateOrganization} className="p-6 space-y-6">
              {/* Organization Overview */}
              <div className="bg-gradient-to-r from-accent-50 to-green-50 rounded-lg p-5 border border-accent-200">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl ${formData.type === 'FMO' ? 'bg-indigo-600' :
                      formData.type === 'EM' ? 'bg-purple-600' :
                        formData.type === 'DM' ? 'bg-green-600' :
                          formData.type === 'CM' ? 'bg-yellow-600' :
                            formData.type === 'BM' ? 'bg-orange-600' :
                              formData.type === 'AM' ? 'bg-red-600' :
                                'bg-gray-600'
                    }`}>
                    {formData.type === 'FMO' ? 'F' :
                      formData.type === 'EM' ? 'E' :
                        formData.type === 'DM' ? 'D' :
                          formData.type === 'CM' ? 'C' :
                            formData.type === 'BM' ? 'B' :
                              formData.type === 'AM' ? 'A' :
                                'M'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-accent-900">{formData.name || "Organization Name"}</h3>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getOrganizationTypeBadge(formData.type)}`}>
                        {formData.type || "Select Type"}
                      </span>
                      <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                        ID: {selectedOrganization.id}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                <h3 className="text-lg font-semibold text-accent-900 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className={`input-field ${formErrors.name ? "border-red-500 ring-2 ring-red-200" : "focus:ring-2 focus:ring-primary-200"}`}
                      placeholder="Enter organization name"
                    />
                    {formErrors.name && (
                      <p className="text-red-600 text-sm mt-1 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2">
                      Organization Type *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => {
                        const newType = e.target.value
                        setFormData(prev => ({ ...prev, type: newType, parentId: undefined }))
                        loadParentOrganizations(newType)
                      }}
                      className={`input-field ${formErrors.type ? "border-red-500 ring-2 ring-red-200" : "focus:ring-2 focus:ring-primary-200"}`}
                    >
                      <option value="">Select type...</option>
                      {availableTypes.map(type => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {formErrors.type && (
                      <p className="text-red-600 text-sm mt-1 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {formErrors.type}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-accent-700 mb-2">
                    Parent Organization
                  </label>
                  {loadingParents ? (
                    <div className="input-field bg-accent-100 animate-pulse h-[42px] flex items-center">
                      <div className="flex items-center space-x-2">
                        <Loader2 className="w-4 h-4 text-primary-600 animate-spin" />
                        <span className="text-sm text-accent-500">Loading parent organizations...</span>
                      </div>
                    </div>
                  ) : parentOrganizations.length > 0 ? (
                    <select
                      value={formData.parentId || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, parentId: e.target.value || undefined }))}
                      className="input-field focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="">None (Top Level)</option>
                      {parentOrganizations.map(org => (
                        <option key={org.id} value={org.id}>
                          {org.name} ({org.type})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="input-field bg-accent-50 text-accent-500 cursor-not-allowed">
                      No parent organizations available
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Settings */}
              <div className="bg-accent-50 rounded-lg p-5 space-y-4 border border-accent-200">
                <h3 className="text-lg font-semibold text-accent-900 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                  Additional Details <span className="text-sm font-normal text-accent-500 ml-2">(Optional)</span>
                </h3>

                <div>
                  <label className="block text-sm font-semibold text-accent-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.settings.description}
                    onChange={(e) => handleSettingsChange("description", e.target.value)}
                    className="input-field focus:ring-2 focus:ring-primary-200"
                    rows={3}
                    placeholder="Brief description of this organization..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2 flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.settings.address}
                      onChange={(e) => handleSettingsChange("address", e.target.value)}
                      className="input-field focus:ring-2 focus:ring-primary-200"
                      placeholder="Organization address..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2 flex items-center">
                      <Phone className="w-4 h-4 mr-1" />
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.settings.phone}
                      onChange={(e) => handleSettingsChange("phone", e.target.value)}
                      className="input-field focus:ring-2 focus:ring-primary-200"
                      placeholder="Contact phone number..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-accent-700 mb-2 flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.settings.email}
                      onChange={(e) => handleSettingsChange("email", e.target.value)}
                      className="input-field focus:ring-2 focus:ring-primary-200"
                      placeholder="Contact email address..."
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-accent-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-5 py-2.5 border border-accent-300 rounded-lg text-accent-700 font-medium hover:bg-accent-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>Update Organization</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Organization Modal */}
      {showDeleteModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-accent-200">
              <h2 className="text-xl font-semibold text-accent-900">Delete Organization</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-accent-400 hover:text-accent-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-accent-900">Delete Organization</h3>
                  <p className="text-sm text-accent-600">
                    This action cannot be undone. This will permanently delete the organization.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <p className="text-sm text-red-800">
                  <strong>Organization:</strong> {selectedOrganization.name} ({selectedOrganization.type})
                </p>
                <p className="text-sm text-red-600 mt-1">
                  ID: {selectedOrganization.id}
                </p>

                {/* Show dependencies if they exist */}
                {((selectedOrganization._count?.agents || 0) > 0 ||
                  (selectedOrganization._count?.teams || 0) > 0 ||
                  (selectedOrganization._count?.campaigns || 0) > 0 ||
                  (selectedOrganization.children?.length || 0) > 0) && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>ℹ️ This organization has dependencies:</strong>
                      </p>
                      <ul className="text-sm text-blue-700 mt-1 ml-4">
                        {(selectedOrganization._count?.agents || 0) > 0 && (
                          <li>• {selectedOrganization._count?.agents || 0} agent(s) assigned</li>
                        )}
                        {(selectedOrganization._count?.teams || 0) > 0 && (
                          <li>• {selectedOrganization._count?.teams || 0} team(s) assigned</li>
                        )}
                        {(selectedOrganization._count?.campaigns || 0) > 0 && (
                          <li>• {selectedOrganization._count?.campaigns || 0} campaign(s) assigned</li>
                        )}
                        {(selectedOrganization.children?.length || 0) > 0 && (
                          <li>• {selectedOrganization.children?.length || 0} child organization(s)</li>
                        )}
                      </ul>
                      <p className="text-sm text-blue-700 mt-2">
                        <strong>As an HR Manager, you can force delete this organization.</strong> All dependencies will be automatically removed.
                      </p>
                    </div>
                  )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-secondary"
                  disabled={!!deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrganization}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  disabled={!!deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>
                      {((selectedOrganization._count?.agents || 0) > 0 ||
                        (selectedOrganization._count?.teams || 0) > 0 ||
                        (selectedOrganization._count?.campaigns || 0) > 0 ||
                        (selectedOrganization.children?.length || 0) > 0)
                        ? "Force Delete Organization"
                        : "Delete Organization"}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reassignment Modal */}
      {showReassignModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Reassign Agents</h3>
              <button
                onClick={() => {
                  setShowReassignModal(false)
                  setSelectedOrganization(null)
                  setReassignToOrgId("")
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-900">Cannot Delete Organization</h4>
                  <p className="text-sm text-gray-600">
                    This organization has dependencies that must be handled first.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                <p className="text-sm text-yellow-800 mb-2">
                  <strong>Organization:</strong> {selectedOrganization.name} ({selectedOrganization.type})
                </p>
                {organizationDependencies && (
                  <div className="text-sm text-yellow-700">
                    <p className="font-medium mb-1">Dependencies:</p>
                    <ul className="ml-4">
                      {organizationDependencies.agents > 0 && (
                        <li>• {organizationDependencies.agents} agent(s) assigned</li>
                      )}
                      {organizationDependencies.teams > 0 && (
                        <li>• {organizationDependencies.teams} team(s) assigned</li>
                      )}
                      {organizationDependencies.campaigns > 0 && (
                        <li>• {organizationDependencies.campaigns} campaign(s) assigned</li>
                      )}
                      {organizationDependencies.children > 0 && (
                        <li>• {organizationDependencies.children} child organization(s)</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {(organizationDependencies?.agents || 0) > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reassign {organizationDependencies?.agents || 0} agent(s) to:
                  </label>
                  <select
                    value={reassignToOrgId}
                    onChange={(e) => setReassignToOrgId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select target organization...</option>
                    {organizations
                      .filter(org => org.id !== selectedOrganization.id)
                      .map(org => (
                        <option key={org.id} value={org.id}>
                          {org.name} ({org.type})
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowReassignModal(false)
                    setSelectedOrganization(null)
                    setReassignToOrgId("")
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                {(organizationDependencies?.agents || 0) > 0 && (
                  <button
                    onClick={handleBulkReassignAgents}
                    disabled={!reassignToOrgId}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Reassign Agents
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List ID Management Modal */}
      {showListIdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-accent-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Tag className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Organization ID Assignment</h2>
                    <p className="text-gray-600 mt-1">Assign organizations to available list IDs (FMO Only)</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowListIdModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Statistics Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
                {loadingListIds ? (
                  // Shimmer effect for statistics
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="p-4 rounded-xl border-2 bg-gray-100 animate-pulse">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="h-6 bg-gray-200 rounded w-8 mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                        </div>
                        <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                      </div>
                    </div>
                  ))
                ) : (
                  listIds.map((listId) => {
                    const stat = Array.isArray(listIdStats) ? listIdStats.find(s => s.listId === listId.name) : null
                    return (
                      <div
                        key={listId.id}
                        className="p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all duration-200"
                        style={(() => {
                          const colors = getListIdColor(listId.name)
                          return {
                            backgroundColor: colors.backgroundColor,
                            borderColor: colors.borderColor,
                            color: colors.textColor
                          }
                        })()}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2 mb-3">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: getListIdColor(listId.name).borderColor }}
                            ></div>
                            <p className="font-bold text-sm uppercase tracking-wide">{listId.name}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-2xl font-bold mb-1">{stat?.count || 0}</p>
                              <p className="text-xs opacity-75 font-medium">Organizations</p>
                            </div>
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${getListIdColor(listId.name).borderColor}20` }}>
                              <Tag className="w-5 h-5" style={{ color: getListIdColor(listId.name).borderColor }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-lg">💡</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-blue-900 mb-2">How to Assign Organization IDs</h4>
                    <div className="space-y-2 text-sm text-blue-800">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Available IDs:</span>
                        <div className="flex space-x-1">
                          {listIds.map(lid => (
                            <span
                              key={lid.id}
                              className="px-2 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${getListIdColor(lid.name).borderColor}20`,
                                color: getListIdColor(lid.name).borderColor,
                                border: `1px solid ${getListIdColor(lid.name).borderColor}`
                              }}
                            >
                              {lid.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      <p><strong>How to assign:</strong> Drag any organization from the list and drop it into one of the colored zones</p>
                      <p><strong>Inheritance:</strong> All EM level organizations will inherit the assigned Organization ID</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content: Two Columns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Organizations List - Scrollable */}
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                    All Organizations
                  </h3>
                  <div className="border border-accent-200 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <div className="space-y-2 p-3">
                        {loadingListIds ? (
                          // Shimmer effect for organizations
                          Array.from({ length: 5 }).map((_, index) => (
                            <div key={index} className="p-3 bg-white border border-accent-200 rounded animate-pulse">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0 space-y-2">
                                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                  <div className="flex items-center space-x-2">
                                    <div className="h-5 bg-gray-200 rounded w-12"></div>
                                    <div className="h-5 bg-gray-200 rounded w-16"></div>
                                  </div>
                                </div>
                                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                              </div>
                            </div>
                          ))
                        ) : (
                          listIdModalOrganizations.map((org) => (
                            <div
                              key={org.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, org)}
                              onDragEnd={handleDragEnd}
                              className={`p-3 bg-white border border-accent-200 rounded cursor-move hover:bg-accent-50 hover:border-accent-300 transition-all ${successfulUpdate === org.id ? 'bg-green-50 border-green-300' : ''
                                }`}
                            >
                              {successfulUpdate === org.id ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-green-600">Updated!</span>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-accent-900 truncate">{org.name}</p>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${org.type === "FMO" ? "bg-purple-100 text-purple-800" :
                                          org.type === "EM" ? "bg-blue-100 text-blue-800" :
                                            org.type === "DM" ? "bg-green-100 text-green-800" :
                                              org.type === "CM" ? "bg-yellow-100 text-yellow-800" :
                                                org.type === "BM" ? "bg-red-100 text-red-800" :
                                                  org.type === "AM" ? "bg-indigo-100 text-indigo-800" :
                                                    "bg-gray-100 text-gray-800"
                                        }`}>
                                        {org.type}
                                      </span>
                                      <span
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                        style={(() => {
                                          const listIdName = getListIdName((org as any).listId)
                                          const colors = getListIdColor(listIdName)
                                          return {
                                            backgroundColor: colors.backgroundColor,
                                            color: colors.textColor,
                                            border: `1px solid ${colors.borderColor}`
                                          }
                                        })()}
                                      >
                                        {getListIdName((org as any).listId)}
                                      </span>
                                    </div>
                                  </div>
                                  <GripVertical className="w-4 h-4 text-accent-400 flex-shrink-0 ml-2" />
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Drop Zones - Scrollable */}
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Tag className="w-5 h-5 mr-2 text-green-600" />
                    Drop Zones
                  </h3>
                  <div className="border border-accent-200 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <div className="space-y-3 p-3">
                        {loadingListIds ? (
                          // Shimmer effect for drop zones
                          Array.from({ length: 5 }).map((_, index) => (
                            <div key={index} className="p-4 rounded-lg border-2 border-dashed min-h-[120px] bg-gray-100 animate-pulse">
                              <div className="flex items-center justify-between mb-3">
                                <div className="h-5 bg-gray-200 rounded w-16"></div>
                                <div className="h-4 bg-gray-200 rounded w-20"></div>
                              </div>
                              <div className="space-y-2">
                                <div className="p-2 bg-white rounded border">
                                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-1"></div>
                                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                </div>
                                <div className="p-2 bg-white rounded border">
                                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-1"></div>
                                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          listIds.map((listId) => (
                            <div
                              key={listId.id}
                              className="p-4 rounded-xl border-2 border-dashed min-h-[100px] transition-all hover:border-solid hover:shadow-lg cursor-pointer"
                              style={(() => {
                                const colors = getListIdColor(listId.name)
                                return {
                                  backgroundColor: colors.backgroundColor,
                                  borderColor: colors.borderColor,
                                  color: colors.textColor
                                }
                              })()}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, listId.name)}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-lg">{listId.name}</h4>
                                <span className="text-sm opacity-75">
                                  {(() => {
                                    const stat = Array.isArray(listIdStats) ? listIdStats.find(s => s.listId === listId.name) : null
                                    return `${stat?.count || 0} organizations`
                                  })()}
                                </span>
                              </div>

                              <div className="space-y-2">
                                {organizationsByListId[listId.name]?.map((org) => (
                                  <div
                                    key={org.id}
                                    className="p-2 bg-white rounded border text-xs"
                                  >
                                    <p className="font-medium truncate">{org.name}</p>
                                    <p className="text-accent-500">{org.type}</p>
                                  </div>
                                )) || []}

                                {(!organizationsByListId[listId.name] || organizationsByListId[listId.name].length === 0) && (
                                  <div className="text-center py-3 text-gray-500">
                                    <div className="w-8 h-8 mx-auto mb-2 border-2 border-dashed rounded-lg flex items-center justify-center" style={{ borderColor: getListIdColor(listId.name).borderColor }}>
                                      <span style={{ color: getListIdColor(listId.name).borderColor, fontSize: '14px' }}>📁</span>
                                    </div>
                                    <p className="text-xs font-medium mb-1">Drop organizations here</p>
                                    <p className="text-[10px] opacity-60">Drag from the list on the left</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
